import { Link, Notice, Select, Stack } from '@eleveo/components-react';
import React from 'react';
import { useField } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';

import { FileInput } from '../../components/Form/FileInput';
import { getUploadFieldInputTag, UPLOAD_FILE_NAME_TAG, UPLOAD_FILE_SIZE_TAG } from './constants';
import { HistoricalDataInterval, historicalDataIntervalToOptions } from './utils';

type DataImportFormProps = {
  disabled: boolean;
  fileName: string | null;
  fileSize: string | null;
  onFileChange: (file: File) => void;
};

/**
 * Data import form body.
 */
export const DataImportForm: React.FC<DataImportFormProps> = ({ disabled, fileName, fileSize, onFileChange }) => {
  const { t } = useTranslation();
  const historicalDataIntervalField = useField('historicalDataInterval');

  return (
    <Stack direction="vertical" className="p-25 w-full" spacing={20}>
      <Notice>
        <Trans i18nKey="dataImport.description">
          Choose CSV file containing <strong>Historical data</strong>
          <br />
          (max. size 32MB)
        </Trans>
      </Notice>

      <FileInput
        buttonTestId={getUploadFieldInputTag('file')}
        fileName={fileName}
        fileNameTestId={UPLOAD_FILE_NAME_TAG}
        fileSize={fileSize}
        fileSizeTestId={UPLOAD_FILE_SIZE_TAG}
        onChange={onFileChange}
        t={t}
      />
      <Select
        className="w-full"
        defaultSelectedKeys={[HistoricalDataInterval.Min15]}
        isDisabled={disabled}
        label={t('dataImport.dataInterval')}
        options={historicalDataIntervalToOptions(t)}
        selectedKeys={historicalDataIntervalField.input.value ? [historicalDataIntervalField.input.value] : []}
        selectionMode="single"
        testId={getUploadFieldInputTag('historicalDataInterval-dropdown')}
        onSelectionChange={(values) => {
          historicalDataIntervalField.input.onChange(values.values().next().value);
        }}
      />

      <Notice>
        <Trans i18nKey="dataImport.hint">
          Please check if your CSV file meets the format and content requirements defined in the Eleveo WFM User Guide
          (Chapter:
          <Link
            target="_blank"
            rel="noopener noreferrer"
            href="https://help.eleveo.com/display/WFM/Importing+Historical+Data"
          >
            Importing Historical Data
          </Link>
          ).
        </Trans>
      </Notice>
    </Stack>
  );
};
